/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Tags 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/tags/Tags.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 6. 22.
 */
import * as parent from '@components/form/field/Field';
import { Select } from '@components/form/field/select/Select';
import { ListStores, TreeStores } from '@models/store/Store';
import { Record } from '@models/store/Record';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import './Tags.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<S extends ListStores | TreeStores, T> extends parent.Properties<T> {
    /**
     * 데이터스토어
     */
    store: S;

    /**
     * 태그값을 표시할 store 의 field 명
     */
    tagField?: string;

    /**
     * 선택된 값을 표시할 store 의 field 명
     */
    displayField?: string;

    /**
     * 폼 전송시 전송될 값을 지정할 store 의 field 명
     */
    valueField?: string;

    /**
     * Select 필드 항목 너비
     */
    selectWidth?: number;

    /**
     * 데이터스토어에 존재하지 않는 값을 태그에 포함시킬지에 관한 여부
     */
    editable?: boolean;

    /**
     * 선택된 항목을 보일 때 사용할 렌더링 함수
     */
    renderer?: (display: string | string[], record: Record, tags: Tags) => string;

    /**
     * 목록 항목을 보일 때 사용할 렌더링 함수
     */
    listRenderer?: (display: string, record: Record, $column: Dom) => string | Promise<string>;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Tags<S extends ListStores | TreeStores = ListStores | TreeStores> extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'tags';

    /**
     * 데이터스토어
     */
    store: S;

    /**
     * 태그값을 표시할 store 의 field 명
     */
    tagField: string | null = null;

    /**
     * 선택된 값을 표시할 store 의 field 명
     */
    displayField: string | null = null;

    /**
     * 폼 전송시 전송될 값을 지정할 store 의 field 명
     */
    valueField: string | null = null;

    /**
     * Select 필드 항목 너비
     */
    selectWidth: number = null;

    /**
     * 데이터스토어에 존재하지 않는 값을 태그에 포함시킬지에 관한 여부
     */
    editable: boolean = false;

    /**
     * 선택된 항목을 보일 때 사용할 렌더링 함수
     */
    renderer: (display: string | string[], record: Record, tags: Tags) => string = null;

    /**
     * Select 필드
     */
    select: Select = null;

    /**
     * 태그 편집을 위해 Select 값을 초기화하는 중인지 여부
     */
    #isInitializingEdit: boolean = false;

    /**
     * Tags 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<S, Tags> = null) {
        super(properties);

        this.store = this.properties.store;
        this.store.retain();
        this.tagField = this.properties.tagField ?? 'tag';
        this.displayField = this.properties.displayField ?? 'display';
        this.valueField = this.properties.valueField ?? 'value';

        this.selectWidth = this.properties.selectWidth ?? null;
        this.editable = this.properties.editable === true;

        this.renderer = this.properties.renderer ?? null;
    }

    /**
     * 태그 DOM 을 가져온다.
     *
     * @return 태그 DOM
     */
    $getTag(record: Record): Dom {
        const $tag = Html.create('div', { 'data-role': 'tag' });

        const $button = Html.create('button', { type: 'button' });

        const $span = Html.create('span');
        if (this.renderer === null) {
            $span.html(record.get(this.tagField));
        } else {
            $span.html(this.renderer(record.get(this.tagField), record, this));
        }

        $button.on('click', async () => {
            if ($tag.getAttr('disabled') === 'disabled') {
                return;
            }

            $tag.append(this.getSelect().$getComponent());
            this.#isInitializingEdit = true;
            await this.getSelect().setValue(record.get(this.valueField));
            this.#isInitializingEdit = false;

            const display = record.get(this.displayField);
            this.getSelect().$getSearch().setValue(display);
            this.getSelect()
                .getList()
                .getStore()
                .setFilters(
                    {
                        [this.displayField]: { value: display, operator: 'likecode' },
                        [this.valueField]: { value: display, operator: 'likecode' },
                    },
                    'OR'
                );

            this.getSelect().focus();
        });

        $button.append($span);

        const $icon = Html.icon('mi mi-close');
        $icon.on('click', async (_, e) => {
            e.stopPropagation();
            if ($tag.getAttr('disabled') === 'disabled') {
                return;
            }

            const index = $tag.getIndex();
            const current: Record[] = (this.value as Record[]) ?? [];
            const next = current.filter((_, i) => i !== index);
            await this.#setValue(next.length > 0 ? next : null);
            $tag.remove();
        });

        $button.append($icon);
        $tag.append($button);

        return $tag;
    }

    /**
     * Select 필드를 가져온다.
     *
     * @returns Select 필드
     */
    getSelect(): Select {
        if (this.store === null) {
            return null;
        }

        if (this.select === null) {
            this.select = new Select({
                search: true,
                store: this.store,
                displayField: this.properties.displayField,
                valueField: this.properties.valueField,
                listRenderer: this.properties.listRenderer,
                fieldWidth: this.selectWidth,
                listeners: {
                    change: async (field) => {
                        if (this.#isInitializingEdit) {
                            return;
                        }
                        const $parent = field.$getComponent().getParent();
                        if ($parent !== null && $parent.getAttr('data-role') === 'tag') {
                            await this.updateTag($parent);
                        } else {
                            await this.addTag();
                        }
                    },
                    blur: async (field) => {
                        const $parent = field.$getComponent().getParent();
                        if ($parent !== null && $parent.getAttr('data-role') === 'tag') {
                            this.$getContent().append(field.$getComponent());
                        }

                        await field.reset(true, null);
                    },
                },
            });

            this.select.$getSearch().on('keydown', async (me, e) => {
                if (e.key == 'Backspace' && me.getValue().length === 0) {
                    const $parent = this.select.$getComponent().getParent();
                    if ($parent !== null && $parent.getAttr('data-role') === 'tag') {
                        return;
                    }

                    const $tag = Html.get('div[data-role="tag"]:last-of-type', this.$getContent());

                    const index = $tag.getIndex();
                    const current: Record[] = (this.value as Record[]) ?? [];
                    const removed = current[index];
                    const next = current.filter((_, i) => i !== index);
                    await this.#setValue(next.length > 0 ? next : null);
                    $tag.remove();

                    if (removed !== undefined) {
                        const display = removed.get(this.displayField);
                        me.setValue(display);
                        this.select
                            .getList()
                            .getStore()
                            .setFilters(
                                {
                                    [this.displayField]: { value: display, operator: 'likecode' },
                                    [this.valueField]: { value: display, operator: 'likecode' },
                                },
                                'OR'
                            );
                    }

                    e.preventDefault();
                    e.stopImmediatePropagation();
                }

                if (e.key == 'Tab' || e.key === ',') {
                    if (this.#isInitializingEdit) {
                        return;
                    }
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    const $parent = this.select.$getComponent().getParent();
                    if ($parent !== null && $parent.getAttr('data-role') === 'tag') {
                        await this.updateTag($parent);
                    } else {
                        await this.addTag();
                    }
                }
            });
        }

        return this.select;
    }

    /**
     * 태그를 수정한다.
     *
     * @param $tag - 수정할 태그 DOM
     */
    async updateTag($tag: Dom): Promise<void> {
        const record = (await this.getSelect().getValueToRecordOne()) ?? (await this.#getRecord());
        if (record === null) {
            return;
        }

        const index = $tag.getIndex();
        const current: Record[] = (this.value as Record[]) ?? [];

        const isDuplicate = current.some(
            (r, i) => i !== index && r.get(this.valueField) === record.get(this.valueField)
        );
        if (isDuplicate) {
            return;
        }

        const next = current.map((r, i) => (i === index ? record : r));
        await this.#setValue(next, false, false);

        const $newTag = this.$getTag(record);
        const $newButton = $newTag.getEl().querySelector('button');
        const $oldButton = $tag.getEl().querySelector('button');
        if ($oldButton && $newButton) {
            $oldButton.replaceWith($newButton);
        }

        this.$getContent().append(this.getSelect().$getComponent());
        await this.getSelect().reset(true, null);
        this.getSelect().focus();
    }

    /**
     * 태그를 추가한다.
     */
    async addTag(): Promise<void> {
        const record = (await this.getSelect().getValueToRecordOne()) ?? (await this.#getRecord());

        if (record === null) {
            return;
        }

        const current: Record[] = (this.value as Record[]) ?? [];
        const isDuplicate = current.some((r) => r.get(this.valueField) === record.get(this.valueField));
        if (isDuplicate) {
            return;
        }

        const next = [...current, record];
        await this.#setValue(next);

        const index = this.getSelect().$getComponent().getIndex();
        this.$getContent().append(this.$getTag(record), index);

        await this.getSelect().reset(true, null);
        this.getSelect().focus();
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값 (valueField 배열)
     * @param is_init - 초기값 여부
     */
    async setValue(value: any[], is_init: boolean = false): Promise<void> {
        if (Array.isArray(value) == false || value.length === 0) {
            await this.#setValue(null, is_init);
            return;
        }

        if (this.store.isLoaded() == false) {
            await this.store.load();
        }

        const records: Record[] = [];
        for (const v of value) {
            const record = await this.store.find({ [this.valueField]: v });
            if (record !== null) {
                records.push(record);
            }
        }

        await this.#setValue(records.length > 0 ? records : null, is_init);
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns valueField 배열
     */
    async getValue(): Promise<any[]> {
        const rawValue = await this.#getValue();

        if (rawValue == null) {
            return null;
        } else {
            const values = [];
            rawValue.forEach((value) => {
                values.push(value.get(this.valueField));
            });
            return values;
        }
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.getSelect().setDisabled(disabled);

        const tags = Html.all('div[data-role="tag"]', this.$getContent());
        tags.forEach(($tag) => {
            $tag.setDisabled(disabled);
        });

        return super.setDisabled(disabled);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.getSelect().$getComponent());
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        if (this.getSelect().isRendered() == false) {
            await this.getSelect().render();
        }

        await super.renderState();
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     * @param is_lock - 큐 예약이 필요한지 여부
     */
    async #setValue(value: Record[], is_init: boolean = false, is_lock: boolean = true): Promise<void> {
        if (is_lock == true) {
            await this.lockValue();
        }

        const next = value == null || value.length == 0 ? null : value;

        if (is_init == true) {
            const $content = this.$getContent();
            Html.all('div[data-role="tag"]', $content).forEach(($tag) => $tag.remove());
            if (next !== null) {
                const selectIndex = this.getSelect().$getComponent().getIndex();
                next.forEach((record, i) => {
                    $content.append(this.$getTag(record), selectIndex < 0 ? null : selectIndex + i);
                });
            }
        }

        if (is_lock == true) {
            await this.releaseValue(next, is_init);
        } else {
            this.value = next;
        }
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async #getValue(): Promise<Record[] | null> {
        if (this.value === null || this.value.length == 0) {
            return null;
        }

        return this.value;
    }

    /**
     * $search 입력값으로 store 에서 레코드를 찾는다.
     *
     * @returns 검색된 레코드 또는 null
     */
    async #getRecord(): Promise<Record | null> {
        const searchValue = this.getSelect().$getSearch().getValue();
        if (!searchValue) {
            return null;
        }

        if (this.store.isLoaded() == false) {
            await this.store.load();
        }

        const found =
            (await this.store.find({ [this.displayField]: searchValue })) ??
            (await this.store.find({ [this.valueField]: searchValue }));

        if (found !== null) {
            return found;
        }

        if (this.editable) {
            return new Record(
                {
                    [this.tagField]: searchValue,
                    [this.displayField]: searchValue,
                    [this.valueField]: searchValue,
                },
                this.store
            );
        }

        return null;
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.store.release();

        await super.remove(removeDom);
    }
}
